import React, {useState, useCallback, useEffect} from 'react';
import {Box, Badge, IconButton, Tooltip} from '@mui/material';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import CancelIcon from '@mui/icons-material/Cancel';
import {MoneyField} from '@/components/common';
import {getAdjustmentTotal} from '@/adjustment/adjust/utils/adjustmentLockUtils';
import type {CommonRow} from '@/services/auditService/interface/wp-row';

export interface AdjustmentCellProps {
    label?: string;
    row: CommonRow;
    currency: string;
    readOnly?: boolean;
    /** 展示模式：true 时有数据则隐藏 addIcon，无数据则只读不可编辑 */
    display?: boolean;
    /** 当没有 adjustments 时，输入框值变化的回调 */
    onAdjustmentChange?: (value: number) => void;
    /** 点击已有 adjustments 的单元格打开详情抽屉 */
    onOpenDrawer?: () => void;
    /** 新增 adjustment 模式下，值变化的回调 */
    onNewAdjustmentValueChange?: (rowId: number | null | undefined, value: number | null) => void;
    /** 外部控制：是否处于新增模式 */
    isAddingNew?: boolean;
    /** 外部控制：设置新增模式 */
    setIsAddingNew?: (adding: boolean) => void;
    /** Optional per-row sign normalization (e.g. TaxWp negative/positive-only rows). */
    normalizeAdjustmentInput?: (value: number) => number;
}

export function AdjustmentCell({
                                   label = '',
                                   row,
                                   currency,
                                   readOnly = false,
                                   display = false,
                                   onAdjustmentChange,
                                   onOpenDrawer,
                                   onNewAdjustmentValueChange,
                                   isAddingNew: externalIsAddingNew,
                                   setIsAddingNew: externalSetIsAddingNew,
                                   normalizeAdjustmentInput,
                               }: AdjustmentCellProps) {
    // 内部状态，当外部没有提供时使用
    const [internalIsAddingNew, setInternalIsAddingNew] = useState(false);
    const [newAdjustmentValue, setNewAdjustmentValue] = useState<number>(0);

    // 使用外部或内部状态
    const isAddingNew = externalIsAddingNew ?? internalIsAddingNew;
    const setIsAddingNew = externalSetIsAddingNew ?? setInternalIsAddingNew;

    // Use ref to store setIsAddingNew to avoid infinite loops
    const setIsAddingNewRef = React.useRef(setIsAddingNew);
    React.useEffect(() => {
        setIsAddingNewRef.current = setIsAddingNew;
    }, [setIsAddingNew]);

    const hasMultiAdj = Array.isArray(row.adjustments) && row.adjustments.length > 0;
    const adjTotal = getAdjustmentTotal(row);

    // 重置新增状态当行变化时
    useEffect(() => {
        if (!hasMultiAdj) {
            setIsAddingNewRef.current(false);
            setNewAdjustmentValue(0);
        }
    }, [row.id, hasMultiAdj]);

    const handleAddClick = useCallback(() => {
        setIsAddingNew(true);
        setNewAdjustmentValue(0);
        onNewAdjustmentValueChange?.(row.id, 0);
    }, [setIsAddingNew, onNewAdjustmentValueChange, row.id]);

    const handleCancelClick = useCallback(() => {
        setIsAddingNew(false);
        setNewAdjustmentValue(0);
        onNewAdjustmentValueChange?.(row.id, null); // null 表示取消
    }, [setIsAddingNew, onNewAdjustmentValueChange, row.id]);

    const applyNormalizedValue = useCallback((value: number) => {
        return normalizeAdjustmentInput ? normalizeAdjustmentInput(value) : value;
    }, [normalizeAdjustmentInput]);

    const handleNewValueChange = useCallback((value: number) => {
        const normalized = applyNormalizedValue(value);
        setNewAdjustmentValue(normalized);
        onNewAdjustmentValueChange?.(row.id, normalized);
    }, [applyNormalizedValue, onNewAdjustmentValueChange, row.id]);

    const handleDirectAdjustmentChange = useCallback((value: number) => {
        onAdjustmentChange?.(applyNormalizedValue(value));
    }, [applyNormalizedValue, onAdjustmentChange]);

    // 情况1：有 adjustments 列表
    if (hasMultiAdj) {
        // 子情况1.1：处于新增模式 - 显示输入框 + cancelIcon
        if (isAddingNew) {
            return (
                <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5}}>
                    <MoneyField
                        value={newAdjustmentValue}
                        showCents
                        align="center"
                        compact
                        currencySymbol={currency}
                        editable={!readOnly}
                        onChangeNumber={handleNewValueChange}
                        onChangeNumberImmediate={normalizeAdjustmentInput ? handleNewValueChange : undefined}
                    />
                    <Tooltip title="Cancel">
                        <IconButton size="small" color="default" onClick={handleCancelClick}>
                            <CancelIcon fontSize="small" sx={{color: 'text.secondary'}}/>
                        </IconButton>
                    </Tooltip>
                </Box>
            );
        }

        // 子情况1.2：显示 total 值 + addIcon（display=true 时隐藏 addIcon）
        return (
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 0.5,
                    cursor: onOpenDrawer ? 'pointer' : 'default',
                    '&:hover': onOpenDrawer ? {backgroundColor: 'action.hover'} : {},
                }}
                onClick={onOpenDrawer}
            >
                <Badge
                    badgeContent={row.adjustments!.length}
                    color="primary"
                    sx={{'& .MuiBadge-badge': {fontSize: 10, height: 16, minWidth: 16}}}
                >
                    <MoneyField
                        label={label}
                        value={adjTotal}
                        showCents
                        align="center"
                        compact
                        currencySymbol={currency}
                        editable={false}
                    />
                </Badge>
                {!readOnly && !display && (
                    <Tooltip title="Add new adjustment">
                        <IconButton
                            size="small"
                            color="primary"
                            onClick={(e) => {
                                e.stopPropagation();
                                handleAddClick();
                            }}
                        >
                            <AddCircleOutlineIcon fontSize="small"/>
                        </IconButton>
                    </Tooltip>
                )}
            </Box>
        );
    }

    // 情况2：没有 adjustments 列表 - 显示可编辑输入框（display=true 时切换为只读）
    return (
        <MoneyField
            label={label}
            value={row.adjustment ?? 0}
            showCents
            align="center"
            compact
            currencySymbol={currency}
            editable={!readOnly && !display}
            onChangeNumber={handleDirectAdjustmentChange}
            onChangeNumberImmediate={normalizeAdjustmentInput ? handleDirectAdjustmentChange : undefined}
        />
    );
}
